import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { PagedList, UserComponentModel } from '../interface';
import { toHttpParams } from '../utils/globals';
import {
  ComponentType,
  CountryModel,
  EmployeeCareerRecordModel,
  EmployeeDiscountResponse,
  Gender,
  MaritalStatus,
  SaveCallCenterUserRequest,
  SaveEmployeeDiscountRequest,
  UserBasicModel,
  UserProfileModel,
  UserResourceService,
  IdentityResourceService
} from '../vn-api';

@Injectable({
  providedIn: 'root'
})
export class HxUserService {
  constructor(
    private httpClient: HttpClient,
    private userResourceService: UserResourceService,
    private identityResourceService: IdentityResourceService,
  ) { }

  getUserById(id: number): Observable<UserBasicModel> {
    return this.httpClient.get<UserBasicModel>(`/api/vn/users/${id}`);
  }

  getUsers(params?: {
    limit?: number;
    components?: ComponentType[];
    query?: string;
    storeId?: number;
    includeDisabled?: boolean;
  }): Observable<PagedList<UserBasicModel>> {
    return this.httpClient.get<PagedList<UserBasicModel>>(`/api/vn/users`, {params: toHttpParams(params, true)});
  }

  getUserByIds(ids: number[]): Observable<PagedList<UserProfileModel>> {
    return this.httpClient.get<PagedList<UserProfileModel>>(`/api/vn/users`, {params: toHttpParams({ids: ids}, true)});
  }

  getUserProfiles(params: {page: number, query: string, storeId: number}): Observable<PagedList<UserProfileModel>> {
    return this.httpClient.get<PagedList<UserProfileModel>>(`/api/vn/users/profiles`, { params: toHttpParams(params, true) });
  }

  removeUserFromStore(userId: number, storeId: number): Observable<any> {
    return this.httpClient.delete(`/api/vn/users/${userId}/stores/${storeId}/memberships`);
  }

  fireUser(userId: number, storeId: number): Observable<any> {
    return this.httpClient.post(`/api/vn/users/${userId}/stores/${storeId}/fire`, {});
  }

  getUserByPhone(phone: string): Observable<UserProfileModel | undefined> {
    return this.httpClient.get<UserProfileModel>(`/api/vn/users/phone/${phone}`);
  }

  resetPassword(userId: number): Promise<void> {
    return firstValueFrom(this.httpClient.post<void>(`/api/vn/users/${userId}/reset-password`, {}));
  }

  getUser(userId: number): Observable<UserProfileModel> {
    return this.httpClient.get<UserProfileModel>(`/api/vn/users/${userId}/full`);
  }

  saveUser(body: UserSaveRequest): Observable<UserProfileModel> {
    return this.httpClient.post<UserProfileModel>(`/api/vn/users`, body);
  }

  deleteMembership(membershipId: number): Observable<any> {
    return this.httpClient.delete(`/api/vn/users/membership/${membershipId}`);
  }

  setDefaultStore(storeId: number): Observable<any> {
    // endpoint moved: /identity/update-store/{id} → /identity/stores/{storeId}/update (updateUserStore)
    return this.identityResourceService.updateUserStore(storeId);
  }

  setOperatorId(asteriskId: number): Observable<UserProfileModel> {
    return this.userResourceService.setOperatorAsterisk(String(asteriskId));
  }

  getOperatorIdList(): Observable<UserBasicModel[]> {
    return this.userResourceService.getOperatorAsterisks() as unknown as Observable<UserBasicModel[]>;
  }

  getManagers(params?: { groupId?: number}): Observable<UserComponentModel[]> {
    // /users/managers is store-based (store_ids, @NotNull) and returns UserBasicModel without an owner flag;
    // the managers popover needs /users/managers-by-group?group_id=... → getUserComponents (UserComponentModel incl. owner)
    return this.userResourceService.getUserComponents(params?.groupId as number) as unknown as Observable<UserComponentModel[]>;
  }

  getEmployees(storeId: number, componentType: string): Observable<UserProfileModel[]> {
    return this.httpClient.get<UserProfileModel[]>(`/api/vn/users/stores/${storeId}/components/${componentType}/employees`);
  }

  saveMemberships(userId: number, body: UserMembershipRequest): Promise<UserProfileModel> {
    return firstValueFrom(this.httpClient.post<UserProfileModel>(`/api/vn/users/${userId}/memberships`, body));
  }

  getCallCenterUsers(countryId: number, query?: string): Promise<UserProfileModel[]> {
    return firstValueFrom(this.userResourceService.getCallCenterUsers(countryId, query));
  }

  fireCallCenterUser(userId: number, countryId: number): Promise<void> {
    return firstValueFrom(this.userResourceService.fireCallCenterUser(userId, countryId));
  }

  saveCallCenterUser(request: SaveCallCenterUserRequest): Promise<UserProfileModel> {
    return firstValueFrom(this.userResourceService.saveCallCenterUser(request));
  }

  getMembershipCountries(userId: number): Promise<CountryModel[]> {
    return firstValueFrom(this.userResourceService.getMembershipCountries(userId));
  }

  getEmployeeDiscount(userId: number, storeId: number): Promise<EmployeeDiscountResponse> {
    return firstValueFrom(this.userResourceService.getEmployeeDiscount(userId, storeId));
  }

  saveEmployeeDiscount(userId: number, request: SaveEmployeeDiscountRequest): Promise<void> {
    return firstValueFrom(this.userResourceService.saveEmployeeDiscount(userId, request));
  }

  removeEmployeeDiscount(userId: number, request: SaveEmployeeDiscountRequest): Promise<void> {
    return firstValueFrom(this.userResourceService.removeEmployeeDiscount(userId, request));
  }

  getManagersByStoreIds(storeIds: number[]): Promise<UserBasicModel[]> {
    return firstValueFrom(this.userResourceService.getManagers(storeIds));
  }

  getCareerRecords(userId: number): Observable<EmployeeCareerRecordModel[]> {
    return this.httpClient.get<EmployeeCareerRecordModel[]>(`/api/vn/users/${userId}/career-records`);
  }

  addCareerRecord(userId: number, body: CareerRecordRequest): Observable<EmployeeCareerRecordModel> {
    return this.httpClient.post<EmployeeCareerRecordModel>(`/api/vn/users/${userId}/career-records`, body);
  }
}

export interface CareerRecordRequest {
  changeDate: string;
  comment: string;
}

export interface UserSaveRequest {
  id: number;
  phone: string;
  email: string;
  firstname: string;
  lastname: string;
  patronymic: string;
  birthdate: string;
  gender: Gender;
  address: string;
  maritalStatus: MaritalStatus;
  storeId: number;
  position: string;
  iin: string;
  hireDate?: string;
  terminationDate?: string;
  /*
  bio: string;
  probations: WorkPeriodModel[];
  documents: DocumentModel[];
  children: ChildrenModel[];
  memberships: MembershipModel[];
  */
}

export interface UserMembershipRequest {
  storeId: number;
  components: ComponentType[];
  employee: boolean;
}
